import { DURATIONS, timeBetween, timeWords, type BuilderTime, type DurationKey } from '@nsb/core';
import { useState } from 'react';

/**
 * How long, in two clicks on a scale from an hour to a year (Jason,
 * 2026-10-04: "all adjustments to settings in the node should be a single or
 * 2 clicks"; "1h 2h 4h at the beginning too"): the shortest it could take, then
 * the longest; the same stop twice for an exact time. Blank until clicked.
 */
export function TimeScale({ value, onChange, label }: { value: BuilderTime | null; onChange: (t: BuilderTime | null) => void; label: string }) {
  const [first, setFirst] = useState<DurationKey | null>(null);
  const W = 330;
  const x0 = 12;
  const step = (W - 26) / (DURATIONS.length - 1);
  const xs = DURATIONS.map((_, i) => x0 + i * step);
  const lo = value ? DURATIONS.findIndex((d) => d.key === value.low) : -1;
  const hi = value ? DURATIONS.findIndex((d) => d.key === value.high) : -1;
  const pick = (k: DurationKey) => {
    if (first === null) {
      setFirst(k);
      onChange({ low: k, high: k });
    } else {
      onChange(timeBetween(first, k));
      setFirst(null);
    }
  };
  return (
    <div>
      <svg viewBox={`0 0 ${W} 40`} className="block w-full max-w-[22rem]" role="group" aria-label={label}>
        <path d={`M${xs[0]} 12 H${xs.at(-1)}`} className="stroke-rule-strong" />
        {xs.map((x, i) => (
          <path key={i} d={`M${x} 8 V16`} className="stroke-rule-strong" />
        ))}
        {lo >= 0 && hi > lo ? <path d={`M${xs[lo]} 12 H${xs[hi]}`} className="stroke-slate" strokeWidth="5" strokeLinecap="round" /> : null}
        {lo >= 0 ? <circle cx={xs[lo]} cy="12" r="5" className="fill-sheet stroke-ink" strokeWidth="1.5" /> : null}
        {hi > lo ? <circle cx={xs[hi]} cy="12" r="5" className="fill-sheet stroke-ink" strokeWidth="1.5" /> : null}
        {first !== null ? <circle cx={xs[DURATIONS.findIndex((d) => d.key === first)]} cy="12" r="8" fill="none" className="stroke-cedar" strokeWidth="1.2" /> : null}
        {DURATIONS.map((d, i) => (
          <g key={d.key} role="button" tabIndex={0} aria-label={d.words} className="cursor-pointer outline-none" onClick={() => pick(d.key)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && pick(d.key)}>
            <rect x={xs[i]! - step / 2} y="0" width={step} height="40" fill="transparent" />
            <text x={xs[i]} y="34" textAnchor="middle" className={`font-mono text-[9.5px] ${i === lo || i === hi ? 'fill-ink' : 'fill-ink-3'}`}>
              {d.short}
            </text>
          </g>
        ))}
      </svg>
      <div className="mt-0.5 flex flex-wrap items-baseline gap-x-4">
        <span className="text-[0.9375rem]">{value ? timeWords(value) : ''}</span>
        {first !== null ? <span className="meta">Now the longest: or the same stop again for exactly that.</span> : null}
        {value ? (
          <button
            type="button"
            className="link text-[0.8125rem] text-ink-2 hover:text-ink"
            onClick={() => {
              setFirst(null);
              onChange(null);
            }}
          >
            Clear
          </button>
        ) : null}
      </div>
    </div>
  );
}
